import { Injectable, computed, inject } from '@angular/core';
import { jwtDecode } from 'jwt-decode';

import { AuthService } from './auth.service';
import { JwtClaims } from './jwt-claims.model';

/**
 * The single source of truth for "what can the current user do," backing both the
 * route-level permissionGuard and the *appHasPermission structural directive so the two
 * can never drift out of sync (UX/UI Design's Navigation Structure). Permission strings
 * match the catalog in FiveWORX.Communication.Contracts.Constants.PermissionNames on the
 * backend -- none are defined there yet in Sprint 1, so this service has nothing
 * meaningful to check against until Sprint 2 adds the first module's permissions.
 */
@Injectable({ providedIn: 'root' })
export class PermissionService {
  private readonly authService = inject(AuthService);

  private readonly claims = computed<JwtClaims | null>(() => {
    const token = this.authService.token();
    return token ? jwtDecode<JwtClaims>(token) : null;
  });

  readonly organizationId = computed(() => this.claims()?.organizationId ?? null);
  readonly userId = computed(() => this.claims()?.sub ?? null);
  readonly permissions = computed(() => this.claims()?.permissions ?? []);

  hasPermission(permission: string): boolean {
    return this.permissions().includes(permission);
  }
}
